import { useCallback, useEffect, useState } from "react";
import type { Order, OrderStatus } from "@/mocks/branch/orders";
import { useBranch } from "@/context/BranchContext";
import {
  ORDER_STATUS,
  PICKUP_METHODS,
  PAYMENT_METHODS,
  SOURCE_LABELS,
  formatTimeAgo,
  formatCountdown,
  nextStatusOf,
  resolveAction,
  statusLabelOf,
  isDeliveryOrder,
  isAppCashOrder,
} from "../constants";
import ConfirmDialog from "./ConfirmDialog";

interface OrderCardProps {
  order: Order;
  onOpen: (id: string) => void;
  onAction: (id: string, status: OrderStatus) => void;
}

export default function OrderCard({ order, onOpen, onAction }: OrderCardProps) {
  const { can } = useBranch();
  const canUpdateStatus = can("updateOrderStatus");
  const status = ORDER_STATUS[order.status];
  const statusLabel = statusLabelOf(order, order.status);
  const action = resolveAction(order);
  const pickup = PICKUP_METHODS[order.pickupMethod];
  const payment = PAYMENT_METHODS[order.paymentMethod];
  const source = SOURCE_LABELS[order.source];
  const isNew = order.status === "new";
  const isDelivery = isDeliveryOrder(order);
  // طلب كاش من التطبيق: لا يُحوَّل تلقائيًا لـ«لم يُستلم» — ينتظر تأكيد الموظف
  const isCashApp = isAppCashOrder(order);

  const [remaining, setRemaining] = useState<number | null>(
    order.status === "ready" && !isDelivery ? (order.pickupRemainingSeconds ?? 900) : null,
  );
  const [confirming, setConfirming] = useState(false);

  const autoNotPicked = useCallback(() => {
    onAction(order.id, "not_picked");
  }, [order.id, onAction]);

  useEffect(() => {
    if (order.status !== "ready" || remaining === null) return;
    if (remaining <= 0) {
      // طلب كاش من التطبيق: لا يتحوّل تلقائيًا لـ«لم يُستلم» — ينتظر إجراء الموظف من تفاصيل الطلب
      if (isCashApp) return;
      autoNotPicked();
      return;
    }
    const timer = window.setTimeout(() => {
      setRemaining((r) => (r === null ? null : r - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [remaining, order.status, isCashApp, autoNotPicked]);

  const countdownTone =
    remaining !== null && remaining <= 300
      ? { color: "#DC2626", bg: "#FEE2E2" }
      : remaining !== null && remaining <= 600
        ? { color: "#B7791F", bg: "#FFF9E6" }
        : { color: "#0D9488", bg: "#E6FFFA" };

  const itemCount = order.items.reduce((sum, i) => sum + i.quantity, 0);

  const handleConfirmAction = () => {
    onAction(order.id, nextStatusOf(order));
    setConfirming(false);
  };

  return (
    <>
      <div
        className={`group relative bg-background-50 rounded-[24px] border border-background-200 overflow-hidden cursor-pointer transition-all duration-200 hover:border-primary-300 hover:-translate-y-1 ${
          isNew ? "animate-pulse-ring" : ""
        }`}
        onClick={() => onOpen(order.id)}
      >
        {/* شريط لون الحالة */}
        <span className="absolute top-0 right-0 left-0 h-1.5" style={{ backgroundColor: status.color }}></span>

        {/* لمسة خفيفة من لون الحالة على رأس الكارد */}
        <span
          className="pointer-events-none absolute top-0 right-0 left-0 h-24"
          style={{ background: `linear-gradient(to bottom, ${status.bg}, transparent)` }}
        ></span>

        <div className="relative p-4 pt-5 flex flex-col gap-3">
          {/* الصف الأول: الرقم + الحالة + الوقت */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[17px] font-extrabold text-foreground-950 tracking-tight" dir="ltr">
                #{order.number}
              </span>
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap"
                style={{ backgroundColor: status.bg, color: status.color }}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={status.icon}></i>
                </div>
                {statusLabel}
              </span>
            </div>
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground-400 whitespace-nowrap shrink-0">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </div>
              {formatTimeAgo(order.minutesAgo)}
            </span>
          </div>

          {/* الصف الثاني: العميل / المصدر */}
          <div className="flex items-center justify-between gap-2 min-w-0">
            {order.source === "app" && order.customerName ? (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 text-[13px] font-bold flex items-center justify-center shrink-0">
                    {order.customerName.trim().charAt(0)}
                  </div>
                  <span className="text-sm font-semibold text-foreground-800 truncate">
                    {order.customerName}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-secondary-500 whitespace-nowrap shrink-0">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={source.icon}></i>
                  </div>
                  {source.short}
                </span>
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary-600">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={source.icon}></i>
                </div>
                {source.label}
              </span>
            )}
          </div>

          {/* الصف الثالث: تفاصيل مختصرة في سطر واحد */}
          <div className="flex items-center gap-x-2 gap-y-1.5 flex-wrap text-[11px] font-medium text-foreground-600 bg-background-100/70 rounded-2xl px-3 py-2">
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                <i className={pickup.icon}></i>
              </div>
              {pickup.label}
            </span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                <i className={payment.icon}></i>
              </div>
              {payment.short}
            </span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                <i className="ri-shopping-basket-line"></i>
              </div>
              {itemCount} منتجات
            </span>
            {order.debtRecorded ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent-50 text-accent-700 font-bold whitespace-nowrap">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-alert-line"></i>
                </div>
                مديونية 0.80
              </span>
            ) : null}
          </div>

          {/* العدّاد (للطلبات الجاهزة للاستلام فقط — لا ينطبق على التوصيل) */}
          {order.status === "ready" && !isDelivery && remaining !== null ? (
            <div
              className="flex items-center justify-between px-3.5 h-10 rounded-2xl"
              style={{ backgroundColor: countdownTone.bg, color: countdownTone.color }}
            >
              <span className="text-[11px] font-semibold">وقت الاستلام المتبقي</span>
              <span className="text-sm font-bold" dir="ltr">
                {formatCountdown(remaining)}
              </span>
            </div>
          ) : null}

          {/* الفوتر: الإجمالي + الإجراء/التقييم/الإغلاق */}
          <div className="pt-2.5 mt-0.5 border-t border-background-200 flex items-center justify-between gap-2">
            <div className="flex items-baseline gap-1 shrink-0">
              <span className="text-xl font-extrabold text-foreground-950 leading-none" dir="ltr">
                {order.total.toFixed(2)}
              </span>
              <span className="text-[11px] font-semibold text-foreground-500">ر.س</span>
            </div>

            {action && canUpdateStatus ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirming(true);
                }}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: action.color, color: action.textColor }}
              >
                {action.label}
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
              </button>
            ) : order.staffRating ? (
              <span className="inline-flex items-center gap-1 shrink-0">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i
                    key={s}
                    className={`text-sm ${
                      s <= order.staffRating!
                        ? "ri-star-fill text-accent-500"
                        : "ri-star-line text-background-400"
                    }`}
                  ></i>
                ))}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-foreground-400 whitespace-nowrap">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={order.status === "picked_up" ? "ri-check-double-line" : "ri-close-line"}></i>
                </div>
                {order.status === "picked_up" ? (isDelivery ? "تم التسليم" : "تم الاستلام") : "لم يُستلم"}
              </span>
            )}
          </div>
        </div>
      </div>

      {confirming && action ? (
        <ConfirmDialog
          title={action.confirmTitle}
          message={action.confirmMessage}
          confirmLabel={action.label}
          tone={order.status === "ready" ? "success" : "primary"}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirming(false)}
          extra={
            order.status === "new" && order.source === "app" && order.paymentMethod === "cash" ? (
              <div className="flex items-start gap-2 p-3 rounded-btn bg-accent-50 border border-accent-200 text-xs text-accent-800 leading-relaxed">
                <i className="ri-information-line text-base mt-0.5 shrink-0"></i>
                <span>
                  سيُسجّل النظام مديونية رسوم استخدام المنصة بقيمة <strong>0.80 ر.س</strong> على
                  المحل عند بدء التحضير (لا تُحتسب على طلبات POS).
                </span>
              </div>
            ) : null
          }
        />
      ) : null}

    </>
  );
}

